import type { State } from 'src/replicant/State';
import {
  CareActionType,
  CareItem,
  CareItemType,
  growthCapsuleUnlockedLevel,
} from './care.ruleset';
import { PetAttribute, PetState, PetStatusEffect } from '../pet';
import * as Pet from '../pet';
import { Widen } from 'src/replicant/utils/types';
import * as Level from '../level';
import * as Fate from '../fate';
import * as Visit from '../visit';
import { getMatch2Timestamp } from '../match2/match2.getters';
import { getMakeHappyCooldownTimestampKey } from '../match2/match2.ruleset';

/**
 * Check if action can be performed
 */
export function canPerformCareAction(
  state: State,
  action: CareActionType | CareItemType | 'growthcapsule' | 'makehappy',
  now: number,
): boolean {
  const petState = Fate.getters.getVisiblePetState(state, now);

  // Want to go out disable care menu
  if (petState === 'want_to_go_out' && action !== 'growthcapsule') {
    return false;
  }

  // If player is dead disable care menu
  if (petState === 'dead') return false;

  // If player is dying allow only first aid and make happy
  if (petState === 'dying' && action !== 'makehappy') {
    return action === 'first_aid';
  }

  // Visiting a friend
  // The pet is in adult stage

  switch (action) {
    case 'food':
    case 'snack': {
      return petState !== 'tooth_decay';
    }

    case 'bath':
    case 'toilet':
      return petState !== 'scared';

    case 'growthcapsule':
      return !getGrowthCapsuleDisabledReason(state, now);

    case 'makehappy':
      return !isMakeHappyOnCooldown(state, now);

    default: {
      return true;
    }
  }
}

/**
 * Get growth capsule disabled reason
 */
export function getGrowthCapsuleDisabledReason(
  state: State,
  now: number,
):
  | 'adult'
  | 'dead'
  | 'dying'
  | 'evolving'
  | 'sick'
  | 'generic'
  | 'visitingFriend'
  | undefined {
  const currentEvolution = Pet.getters.getCurrentEvolution(state);
  const currentLevel = Level.getters.getCurrentLevel(state);

  if (!currentEvolution || currentLevel < growthCapsuleUnlockedLevel) {
    return 'generic';
  }

  const isVisiting = Visit.getters.isVisiting(state);
  const petState = Fate.getters.getVisiblePetState(state, now);
  if (petState === 'evolving') {
    return 'evolving';
  }

  if (petState === 'dead') {
    return 'dead';
  }

  if (petState === 'dying') {
    return 'dying';
  }

  if (petState === 'sick') {
    return 'sick';
  }

  if (isVisiting) {
    return 'visitingFriend';
  }

  if (currentEvolution.stage === 'adult') {
    return 'adult';
  }

  return undefined;
}
/**
 * Return true if trying to cure one of diseases(sick, tooDecay, dying)
 *
 * @param targetStatusEffects
 * @param previousPetState
 *
 * @returns boolean
 */
export function hasTriedToCurePet(
  targetStatusEffects: PetStatusEffect[],
  previousPetState: PetState,
): boolean {
  const triedToCureSickness =
    targetStatusEffects?.includes('sick') && previousPetState === 'sick';
  const triedToCureToothDecay =
    targetStatusEffects?.includes('toothDecay') &&
    previousPetState === 'tooth_decay';
  const triedToCureDying =
    targetStatusEffects?.includes('dying') && previousPetState === 'dying';

  return triedToCureSickness || triedToCureToothDecay || triedToCureDying;
}

/**
 * Return result for care action analytics
 *
 * @param hasTriedToCurePet - See hasTriedToCurePet
 * @param resolvedStatusEffect - Is status effect has been resolved
 *
 * @returns Return 'success' or 'failure' or undefined(if it's 100% resolvable effect)
 */
export function getResultOfCureAction(
  hasTriedToCurePet: boolean,
  resolvedStatusEffect: boolean,
): 'success' | 'failure' | undefined {
  const result = resolvedStatusEffect ? 'success' : 'failure';
  // Only track if we tried to cure visible state.
  return hasTriedToCurePet ? result : undefined;
}

/**
 * Get care item effectiveness
 */
export function getCareItemAmount(
  careItem: Widen<CareItem>,
  attribute: PetAttribute,
): number | undefined {
  switch (attribute) {
    case 'love': {
      return careItem.love;
    }
    case 'stomach': {
      return careItem.stomach;
    }
    case 'toothAcidity': {
      return careItem.tooth_acidity;
    }
    default: {
      return 0;
    }
  }
}

function getMakeHappyRemainingCooldown(state: State, now: number) {
  const timestamp = getMatch2Timestamp(
    state,
    getMakeHappyCooldownTimestampKey(),
  );
  return Math.max(0, timestamp - now);
}

function isMakeHappyOnCooldown(state: State, now: number) {
  return getMakeHappyRemainingCooldown(state, now) > 0;
}
